import type {
  DispatchFeedFigure,
  DispatchMetric,
  DispatchMetricData,
  DispatchMetricSeries,
} from '../../api/types';
import type { CivilDay } from './frames';
import { categoryLabel, displayName, seriesMatches, subcategoryLabel } from './metricMeta';

/**
 * What one card reads out of the page's batched answers, and what it calls
 * itself — the questions every card asks the same way, kept out of the
 * component so a card and the panel that takes it apart cannot answer them
 * differently.
 */

/** `<input type="date">` speaks `YYYY-MM-DD`, which is also how a civil day is written here. */
export const parseDayInput = (value: string): CivilDay | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  return { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) };
};

export interface Answer {
  series: DispatchMetricSeries[];
  error: string | null;
}

/**
 * One category's answer out of a batch — its series, or the reason it has
 * none. A category the batch did not mention answers nothing and no error,
 * which the card then draws as an empty window rather than a failure.
 */
export const answerFor = (rows: DispatchMetricData[] | null, category: string): Answer => {
  const row = rows?.find((entry) => entry.category === category.toLowerCase());
  return { series: row?.series ?? [], error: row?.error ?? null };
};

/**
 * The card's own series, narrowed out of the category batch it shares.
 *
 * Case-blind, because the codes a metric stores and the ones dispatch answers
 * with have not always agreed on case. Customers and affiliates disagree on
 * the word too — the API answers `CUSTOMERS` / `AFFILIATES` for a card that
 * asked for `NEW_CUSTOMER` / `NEW_AFFILIATE` — and dropping that series draws
 * an empty card.
 */
export const cardSeries = (series: DispatchMetricSeries[], codes: string[]): DispatchMetricSeries[] =>
  series.filter((row) => codes.some((entry) => seriesMatches(entry, String(row.subcategory))));

/**
 * A dispatch card's answer as ONE series, which is what makes it drawable by
 * the same builders as every other card.
 *
 * One row, never one per bucket. The count the server sent is a union — a job
 * that is both a farm-out and a multi-stop counts once — and splitting it back
 * into a row per subcategory would let `buildKpi`, which sums across codes,
 * count that job again for each bucket it is in.
 *
 * The row is keyed on the card's single bucket where it has one, so the legend
 * and the axis read the bucket's own name; a card drawing several is keyed on
 * its own title instead, because no one bucket's name is honest about it.
 */
export const feedSeries = (
  figure: DispatchFeedFigure,
  title: string,
): { code: string; rows: DispatchMetricSeries[] } => {
  const code = figure.codes.length === 1 ? figure.codes[0] : title;
  return { code, rows: [{ subcategory: code, value: figure.points }] };
};

/** The card's name: what somebody called it, else what it counts. */
export const cardTitle = (metric: Pick<DispatchMetric, 'metricsName' | 'category'>): string =>
  displayName(metric.metricsName) || categoryLabel(metric.category);

/**
 * The line under a card's title, unless it says the same thing as the title.
 *
 * A single-subcategory metric is usually NAMED after it, and "New Jobs" over
 * "New Jobs" is a card that repeats itself where the second line should have
 * been telling you what went into the number — so the category says it then.
 */
export const cardSubtitle = (codes: string[], title: string, category: string): string => {
  const codeLine = codes.map(subcategoryLabel).join(' · ');
  return codeLine && codeLine.toLowerCase() !== title.toLowerCase() ? codeLine : categoryLabel(category);
};

/** "Back a day", "Forward 7 days" — how far one press of an arrow moves the window. */
export const stepLabel = (direction: -1 | 1, days: number): string =>
  `${direction < 0 ? 'Back' : 'Forward'} ${days === 1 ? 'a day' : `${days} days`}`;
